import { useMemo, useState } from 'react';

/**
 * Hook genérico de ordenación cliente para arrays de objetos.
 *
 *   const { sorted, sortKey, sortDir, toggle } = useSortable(items, 'razon_social');
 *
 * - Soporta acceso anidado vía función accessor en `accessors` por clave:
 *     useSortable(items, 'created_at', 'desc', { entidad: (r) => r.entidad?.razon_social })
 * - Strings se comparan con `localeCompare('es')`.
 * - null / undefined siempre van al final.
 */
export function useSortable(items, defaultKey = '', defaultDir = 'asc', accessors = {}) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const toggle = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey || !Array.isArray(items)) return items || [];
    const get = accessors[sortKey] || ((row) => row?.[sortKey]);
    const arr = [...items];
    arr.sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      // null/undefined al final
      const aEmpty = va === null || va === undefined || va === '';
      const bEmpty = vb === null || vb === undefined || vb === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      let cmp;
      if (typeof va === 'string' && typeof vb === 'string') {
        cmp = va.localeCompare(vb, 'es', { numeric: true, sensitivity: 'base' });
      } else if (va instanceof Date && vb instanceof Date) {
        cmp = va.getTime() - vb.getTime();
      } else {
        // Tratar como número o comparable
        cmp = va > vb ? 1 : va < vb ? -1 : 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [items, sortKey, sortDir, accessors]);

  return { sorted, sortKey, sortDir, toggle };
}

export default useSortable;
